import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { makeStyles, TextField, Select, MenuItem } from '@material-ui/core';
import { PeriodTypeToString } from '../SessionPeriods';
import SelectSingleLineupEntry from '../../Util/SelectSingleLineupEntry';
import { ClassType, SessionEventReason, PeriodType } from '../../../api';
import { TSession } from '../Sessions';
import { TLabelTask } from './SessionLabelTask';
import { LabelTask, LineupEntry, Period, timeSpanToSeconds } from '../../../services/sessionModels';
import labelTasksService, { labelTaskInput } from '../../../services/labelTasks';
import MatchInfoButton from '../MatchInfoButton';
import { findPeriodForFrames } from './sessionLabelOverviewReducer';
import { ReactComponent as ShirtIcon } from '../../../graphics/shirt.svg';
import useHasPrivilege from '../../Util/useHasPrivilege';
import { Privilege } from '../../../api';

const useStyles = makeStyles(() => ({
  sidebarRow: {
    padding: '8px 0px',
  },
  icon: {
    marginTop: '8px',
    width: 28,
    height: 28,
  },
}));

type Props = {
  session: TSession;
  task: TLabelTask;
  playTime: number;
  lineup: LineupEntry[];
  labelTasks: LabelTask[];
  reloadTask: () => void;
  onChangeLineupEntry?: (value: LineupEntry) => void;
};

const LabelPlayerSidebar = (props: Props) => {
  const classes = useStyles();
  const isAdmin = useHasPrivilege(Privilege.Admin);

  const { session, task, playTime, lineup, labelTasks, reloadTask, onChangeLineupEntry } = props;
  const [labelLineupIdsUnavailable, setLabelLineupIdsUnavailable] = useState<Array<number>>([]);

  const fps: number = (session.videos && session.videos.length > 0 && session.videos[0].fps) || 25;
  const [classType, setClassType] = useState<ClassType>((task.classType as ClassType) || ClassType.Referee);
  let color: string | undefined | null = "";
  let trackType: string = "";
  let teamName: string | undefined | null = "";

  switch(classType) {
    case ClassType.Referee:
      color = session.refereeColor;
      trackType = "Official";
      teamName = "Official";
      break;
    case ClassType.HomeKeeper:
      color = session.homeKeeperColor;
      trackType = "Goalkeeper";
      teamName = session.homeTeam?.name;
      break;
    case ClassType.HomePlayer:
      color = session.homePlayerColor;
      trackType = "Player";
      teamName = session.homeTeam?.name;
    break;
    case ClassType.AwayKeeper:
      color = session.awayKeeperColor;
      trackType = "Goalkeeper";
      teamName = session.awayTeam?.name;
      break;
    case ClassType.AwayPlayer:
      color = session.awayPlayerColor;
      trackType = "Player";
      teamName = session.awayTeam?.name;
      break;
  }

  const getClassType = useCallback((teamName: String) => {
    if (teamName === session.awayTeam?.name && trackType === "Player") {
      return ClassType.AwayPlayer;
    }
    
    if (teamName === session.awayTeam?.name && trackType === "Goalkeeper") {
      return ClassType.AwayKeeper;
    }

    if (teamName === session.homeTeam?.name && trackType === "Player") {
      return ClassType.HomePlayer;
    }
    
    if (teamName === session.homeTeam?.name && trackType === "Goalkeeper") {
      return ClassType.HomeKeeper;
    }

    return ClassType.Referee;
  }, [session, trackType]);

  const foundPeriod: Period | undefined | null = findPeriodForFrames(session, task.startFrame, task.endFrame);

  useEffect(() => {
    if (foundPeriod) {
      const offsetSeconds: number = timeSpanToSeconds(session.startAndFinishOffset);
      const periodStartFrame: number = Math.round(((foundPeriod.startTimeInMilliseconds || 0)/1000 - offsetSeconds) * fps);
      const periodEndFrame: number = Math.round(((foundPeriod.endTimeInMilliseconds || 0)/1000 + offsetSeconds) * fps);

      // Get already taken identitites from label tasks
      let alreadyTaken: number[] =
        labelTasks
          .filter((l) => l.lineupEntry && l.startFrame >= periodStartFrame && l.startFrame <= periodEndFrame)
          .map((l) => l.lineupEntry!.id);

      // Get the ones from the lineup who are already substituted out
      const areNotPlayingEvent: number[] =
        lineup.map((l) => {
          //Determine when this player leaves the pitch in video time
          let outtime = 10000000; //Normally a player is on the pitch the entire match
          if (
            l.outEvent &&
            l.outEvent.timestamp &&
            (l.outEvent.reason === SessionEventReason.Injury || l.outEvent.reason === SessionEventReason.RedCard)
          ) {
            outtime = timeSpanToSeconds(l.outEvent.timestamp) * 1000;
          }
          return outtime + 120 < playTime ? l.id : 0; //Safety margin of extra 120s in case too much has been labeled
        });
      // NOTE 12-10-2022 - This doesn't do anything since result is not assigned. Test if we still need it.
      alreadyTaken.concat(areNotPlayingEvent);

      // Get identities who don't play this period
      const areNotPlayingPeriod: number[] =
        lineup.filter((l) => {
          return (l.inEvent && Math.round(timeSpanToSeconds(l.inEvent.timestamp) * fps) > periodEndFrame)
              || (l.outEvent && Math.round(timeSpanToSeconds(l.outEvent.timestamp) * fps) < periodStartFrame)
              || (foundPeriod.type !== PeriodType.FirstHalf && l.outEvent?.offscreen);
        }).map((l) => l.id);
      alreadyTaken = alreadyTaken.concat(areNotPlayingPeriod);

      console.log('ffff', alreadyTaken);

      console.log("Already taken: ", alreadyTaken, " - not playing period: ", areNotPlayingPeriod);

      setLabelLineupIdsUnavailable(alreadyTaken.sort((a, b) => a - b));
    }
  }, [foundPeriod, session, fps, playTime, classType, lineup, labelTasks]);

  const updateTaskIdentity = useCallback(
    async (lineupentry?: LineupEntry) => {
      await labelTasksService.updateLabelTask(task.id, {
        ...labelTaskInput(task),
        classType: classType,
        lineupEntryId: lineupentry?.id ?? null,
      });
      reloadTask();
    },
    [task, classType, reloadTask]
  );

  const onLineupEntrySelect = useCallback(
    (lineupentry: LineupEntry | null) => {
      if (!lineupentry) {
        updateTaskIdentity(undefined);
        return;
      }

      // Callback
      onChangeLineupEntry && onChangeLineupEntry(lineupentry);
    },
    [updateTaskIdentity, onChangeLineupEntry]
  );

  const matchInfoButton = useMemo(() => {
    return <MatchInfoButton matchInfoUrl={session?.matchInfoUrl} />;
  }, [session.matchInfoUrl]);

  const updateClassType = useCallback((event) => {
    const classType = getClassType(event.target.value);
      setClassType(classType)

  }, [getClassType, setClassType])

  return (
    <React.Fragment>
      <MyTextField clazz={classes.sidebarRow} value={String(task.id)} label="Task id" disabled>
        {matchInfoButton}
      </MyTextField>
      <MyTextField
        clazz={classes.sidebarRow}
        value={foundPeriod ? PeriodTypeToString(foundPeriod.type) : '-'}
        label="Period"
        disabled
      />

      {isAdmin && <> 
          <Select className={classes.sidebarRow} style={{ width: 180 }}
            value={teamName}
            onChange={updateClassType}
          >
            <MenuItem value={session.homeTeam?.name ?? undefined}>{session.homeTeam?.name}</MenuItem>
            <MenuItem value={session.awayTeam?.name ?? undefined}>{session.awayTeam?.name}</MenuItem>
          </Select>

          <ShirtIcon className={classes.icon} fill={color || "#f0f"} />
        </>
      }

      {!isAdmin && teamName && <MyTextField clazz={classes.sidebarRow} value={teamName} label="Team" disabled>
        <ShirtIcon className={classes.icon} fill={color || "#f0f"} />
      </MyTextField>}

      <MyTextField clazz={classes.sidebarRow} value={trackType} label="Type" disabled />
      <div className={classes.sidebarRow} style={{ width: 180 }}>
        <SelectSingleLineupEntry
          value={lineup.find((l) => l.id === task.lineupEntry?.id)}
          onChange={onLineupEntrySelect}
          label="Identity"
          exclude={labelLineupIdsUnavailable}
          lineup={lineup}
          classType={classType}
        />
      </div>
    </React.Fragment>
  );
};

type MyTextFieldProps = {
  clazz: string;
  value: string | null | undefined;
  label: string;
  disabled: boolean;
};

const MyTextField: React.FC<MyTextFieldProps> = React.memo(({ clazz, value, label, disabled, children }) => {
  return (
    <div className={clazz}>
      <TextField value={value} label={label} disabled={disabled} />
      {children}
    </div>
  );
});

export default LabelPlayerSidebar;
